/**
 * Dosage for the S0 chord-presentation view.
 *
 * ## The problem this solves
 *
 * S0 runs once per phrase — 20 to 40 times on a real piece. A screen that
 * shows the same amount of material every time trains the player to skip
 * it, and by the time a phrase genuinely needs explaining they are no
 * longer reading. So S0 has three densities, and the interesting one
 * (`dense`) is spent only where it buys something.
 *
 * ## Why the rule keys on the phrase index
 *
 * Density is a pure function of "what the player has necessarily met
 * before reaching phrase k", not of what they actually visited. Keying on
 * visit history would make the screen change under them when they step
 * back through the stepper, which reads as a bug. Index-based means the
 * same phrase always presents the same way.
 */

import type { AnalysisChord } from "@/lib/music/analyzer-types";
import type { Phrase } from "@/lib/learning-journey/types";
import { selectPhraseChords } from "./phrase-chords";

// ── Public model ──────────────────────────────────────────────────

export type S0Density = "dense" | "standard" | "flash";

export type S0Dosage = {
  readonly density: S0Density;
  /** Symbols of this phrase never met in phrases `0..k-1`. Drives the `NOUVEAU` chip. */
  readonly newSymbols: ReadonlySet<string>;
  /**
   * 1-based index of the earlier phrase sharing this degree sequence,
   * or `null`. Feeds "Même enchaînement que la phrase 4."
   */
  readonly repeatOfPhrase: number | null;
  /**
   * The analysis is too busy to narrate (many chords, or chords shorter
   * than a beat). Suppresses glosses and the explanation line even in
   * `standard` — a wrong story is worse than no story.
   */
  readonly busy: boolean;
};

/** Above this many chords in one phrase, narration stops making sense. */
const BUSY_CHORD_COUNT = 6;

// ── Public API ────────────────────────────────────────────────────

export type S0DosageInput = {
  readonly chords: readonly AnalysisChord[];
  readonly phrases: readonly Phrase[];
  readonly phraseIndex: number;
  /** Ticks per beat, used to spot chords too short to talk about. */
  readonly ticksPerBeat: number;
};

/**
 * Resolve how much S0 should show for the current phrase.
 *
 * Base rule, in order:
 *   - first phrase                          → dense (nothing is known yet)
 *   - degree sequence already met            → flash
 *   - carries a chord symbol never met       → dense
 *   - otherwise                              → standard
 *
 * Then the guard: a busy analysis caps at `standard` and flips `busy`.
 */
export function computeS0Dosage(input: S0DosageInput): S0Dosage {
  const { chords, phrases, phraseIndex, ticksPerBeat } = input;

  const current = phrases[phraseIndex];

  if (current === undefined) {
    return {
      density: "standard",
      newSymbols: new Set(),
      repeatOfPhrase: null,
      busy: false,
    };
  }

  const currentChords = phraseChordsOf(chords, current);
  const seenSymbols = new Set<string>();
  const seenSignatures = new Map<string, number>();

  for (let j = 0; j < phraseIndex; j++) {
    const previous = phrases[j];

    if (previous === undefined) {
      continue;
    }

    const previousChords = phraseChordsOf(chords, previous);

    for (const chord of previousChords) {
      seenSymbols.add(chord.symbol);
    }

    const signature = phraseSignature(previousChords, previous);

    if (signature !== null && !seenSignatures.has(signature)) {
      // First occurrence wins: pointing at the earliest phrase gives the
      // player a stable landmark to go back to.
      seenSignatures.set(signature, j + 1);
    }
  }

  const newSymbols = new Set<string>();

  // On the first phrase every chord is technically new, which makes the
  // marker meaningless: it exists to flag an exception, and "all of them"
  // is not one. Nothing precedes phrase 0, so nothing can stand out in it.
  if (phraseIndex > 0) {
    for (const chord of currentChords) {
      if (!seenSymbols.has(chord.symbol)) {
        newSymbols.add(chord.symbol);
      }
    }
  }

  const signature = phraseSignature(currentChords, current);
  const repeatOfPhrase =
    signature === null ? null : seenSignatures.get(signature) ?? null;

  const busy = isBusy(currentChords, current, ticksPerBeat);
  const density = resolveDensity({
    phraseIndex,
    repeatOfPhrase,
    hasNewSymbol: newSymbols.size > 0,
    busy,
  });

  return { density, newSymbols, repeatOfPhrase, busy };
}

// ── Internals ─────────────────────────────────────────────────────

function resolveDensity(input: {
  phraseIndex: number;
  repeatOfPhrase: number | null;
  hasNewSymbol: boolean;
  busy: boolean;
}): S0Density {
  const base = baseDensity(input);

  if (input.busy && base === "dense") {
    return "standard";
  }

  return base;
}

function baseDensity(input: {
  phraseIndex: number;
  repeatOfPhrase: number | null;
  hasNewSymbol: boolean;
}): S0Density {
  if (input.phraseIndex === 0) {
    return "dense";
  }

  if (input.repeatOfPhrase !== null) {
    return "flash";
  }

  if (input.hasNewSymbol) {
    return "dense";
  }

  return "standard";
}

function phraseChordsOf(
  chords: readonly AnalysisChord[],
  phrase: Phrase,
): AnalysisChord[] {
  return selectPhraseChords(chords, phrase.startTick, phrase.endTick);
}

/**
 * Identity of a phrase for the purpose of "have I already met this?".
 *
 * ## Why the degree sequence is not enough
 *
 * It was, at first, and it was wrong on exactly the repertoire this app
 * targets. Pop, film and game music are built on loops: *Comptine d'un
 * autre été* runs `iv-VI-i-III` for all 57 bars, so 12 of its 14 phrases
 * claimed to repeat the first one — including phrases whose note count
 * jumps from 48 to 112. The harmony did repeat; the phrase did not, and
 * the player was told "you know this one" in front of material they had
 * never seen.
 *
 * So identity is "same chords **and** same keys under the hands". The
 * pitch set is taken per hand, because the same notes redistributed
 * between the hands is a different thing to play.
 *
 * Deliberately *not* part of the identity: rhythm and note counts. A
 * phrase restating the same idea with one ornament added is still the
 * same idea harmonically, and that is all this screen claims. Being
 * slightly too eager to call two phrases identical costs one skipped
 * presentation; being too strict costs nothing but shows the long screen
 * forever, which is what the whole dosage mechanism exists to avoid.
 *
 * Returns `null` when the phrase cannot serve as an identity: a single
 * chord is not a progression worth recognising, and an empty roman
 * numeral means the analyzer had no opinion — matching two phrases on
 * "no opinion at all" would declare them the same when we know nothing
 * about either. Both keep the phrase out of `flash`.
 */
function phraseSignature(
  chords: readonly AnalysisChord[],
  phrase: Phrase,
): string | null {
  if (chords.length < 2) {
    return null;
  }

  if (chords.some((c) => c.romanNumeral === "")) {
    return null;
  }

  const degrees = chords.map((c) => c.romanNumeral).join("-");
  const left = pitchSet(phrase.notesByHand.left);
  const right = pitchSet(phrase.notesByHand.right);

  return `${degrees}|${left}|${right}`;
}

function pitchSet(notes: readonly { readonly midi: number }[]): string {
  return [...new Set(notes.map((n) => n.midi))]
    .sort((a, b) => a - b)
    .join(",");
}

function isBusy(
  chords: readonly AnalysisChord[],
  phrase: Phrase,
  ticksPerBeat: number,
): boolean {
  if (chords.length > BUSY_CHORD_COUNT) {
    return true;
  }

  if (ticksPerBeat <= 0) {
    return false;
  }

  return chords.some((chord) => {
    const start = Math.max(chord.tick, phrase.startTick);
    const end = Math.min(chord.endTick, phrase.endTick);

    return end - start < ticksPerBeat;
  });
}
